<template>
  <div>
    <!-- Ventana de chat moderna -->
    <v-slide-y-reverse-transition>
      <v-card
        v-show="open"
        class="chatbot-window elevation-12"
        style="position: fixed; bottom: 100px; right: 20px; width: 380px; max-height: 600px; z-index: 9998; display: flex; flex-direction: column; border-radius: 16px; overflow: hidden;"
      >
        <!-- Header azul -->
        <v-card-title class="primary white--text py-3 px-4">
          <v-icon left dark>mdi-robot-outline</v-icon>
          <div class="flex-grow-1">
            <div class="subtitle-1 font-weight-medium">Asistente Pivot Fleet</div>
            <div class="caption d-flex align-center" style="margin-top: -2px;">
              <span class="status-dot mr-1"></span>
              Con IA · Siempre disponible
            </div>
          </div>
          <v-btn v-if="messages.length > 1" icon dark @click="clearCache" title="Nuevo chat">
            <v-icon>mdi-message-plus-outline</v-icon>
          </v-btn>
          <v-btn icon dark @click="open = false" title="Cerrar">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <!-- Entorno Grupo 5.11: el Administrador de Grupo puede filtrar por concesionario -->
        <div v-if="isGroupAdmin" class="px-3 pt-3">
          <v-select
            v-model="dealerId"
            :items="dealers"
            item-text="name"
            item-value="id"
            label="Datos de"
            placeholder="Todo el grupo"
            clearable
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-store"
          ></v-select>
        </div>

        <!-- Área de mensajes -->
        <div class="messages-area pa-3" ref="messages">
          <div v-for="(msg, i) in messages" :key="i" class="message-wrapper mb-3">
            <div :class="msg.isUser ? 'message-user' : 'message-bot'">
              <div v-if="!msg.isUser" class="bot-avatar">
                <v-avatar size="28" color="primary lighten-4">
                  <v-icon size="16" color="primary">mdi-robot</v-icon>
                </v-avatar>
              </div>
              <div class="message-bubble" :class="msg.isUser ? 'user-bubble' : 'bot-bubble'">
                <span v-if="msg.isUser">{{ msg.text }}</span>
                <span v-else v-html="msg.rendered" class="bot-content"></span>
                <div v-if="!msg.isUser && msg.suggestions && msg.suggestions.length" class="suggestions-wrapper">
                  <v-chip
                    v-for="(suggestion, j) in msg.suggestions"
                    :key="j"
                    outlined
                    color="primary"
                    small
                    class="suggestion-chip"
                    @click="sendSuggestion(suggestion)"
                  >
                    {{ suggestion }}
                  </v-chip>
                </div>
                <div class="message-time">{{ msg.time }}</div>
              </div>
            </div>
          </div>
          
          <!-- Indicador de escritura -->
          <div v-if="loading" class="message-wrapper mb-3">
            <div class="message-bot">
              <div class="bot-avatar">
                <v-avatar size="28" color="primary lighten-4">
                  <v-icon size="16" color="primary">mdi-robot</v-icon>
                </v-avatar>
              </div>
              <div class="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        </div>

        <v-divider></v-divider>

        <!-- Input moderno -->
        <v-card-actions class="pa-3">
          <v-text-field
            v-model="message"
            hide-details
            solo
            flat
            dense
            placeholder="Escribe tu mensaje..."
            class="message-input"
            @keyup.enter="sendMessage"
          ></v-text-field>
          <v-btn
            icon
            color="primary"
            :disabled="!message.trim() || loading"
            @click="sendMessage"
            class="ml-2"
          >
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-slide-y-reverse-transition>
  </div>
</template>

<script>
import ChatBotService from '../services/ChatBotService'
import GrupoPanelService from '../services/GrupoPanelService'

function parseSuggestions (text) {
  const match = text.match(/\[SUGERENCIAS:\s*(.+?)\]\s*$/s)
  if (!match) return { clean: text, suggestions: [] }
  const suggestions = match[1].split('|').map(s => s.trim().replace(/^"(.*)"$/, '$1'))
  const clean = text.replace(match[0], '').trim()
  return { clean, suggestions }
}

function renderMarkdown (text) {
  const lines = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .split('\n')
  const out = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]
    const h3 = line.match(/^### (.+)/)
    const h2 = line.match(/^## (.+)/)
    const h1 = line.match(/^# (.+)/)
    const orderedMatch = line.match(/^\d+\. (.+)/)
    const unorderedMatch = line.match(/^- (.+)/)
    if (h1) { out.push(`<h1>${h1[1]}</h1>`); i++; continue }
    if (h2) { out.push(`<h2>${h2[1]}</h2>`); i++; continue }
    if (h3) { out.push(`<h3>${h3[1]}</h3>`); i++; continue }
    if (orderedMatch || unorderedMatch) {
      const isOrdered = !!orderedMatch
      const items = []
      while (i < lines.length) {
        const l = lines[i]
        const m = isOrdered ? l.match(/^\d+\. (.+)/) : l.match(/^- (.+)/)
        if (!m) break
        items.push(m[1])
        i++
      }
      const tag = isOrdered ? 'ol' : 'ul'
      out.push(`<${tag}>${items.map(item => `<li>${item}</li>`).join('')}</${tag}>`)
    } else {
      out.push(line || '<br>')
      i++
    }
  }
  return out.join('')
}

export default {
  name: 'ChatBot',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    open: false,
    message: '',
    loading: false,
    sessionId: null,
    historial: [],
    messages: [],
    dealers: [],
    dealerId: null
  }),

  computed: {
    // Administrador de Grupo en su panel: el asistente responde con los datos del grupo
    isGroupAdmin () {
      return this.$store.getters.IS_GROUP_ADMIN
    }
  },
  created () {
    // Cargar datos del localStorage
    this.loadFromCache()
    
    // Si no hay session_id, crear uno nuevo
    if (!this.sessionId) {
      this.sessionId = self.crypto.randomUUID()
      this.saveToCache()
    }
    
    // Si no hay mensajes, agregar el mensaje de bienvenida
    if (this.messages.length === 0) {
      this.messages.push({
        text: '¡Hola! ¿En qué puedo ayudarte?',
        isUser: false,
        rendered: renderMarkdown('¡Hola! ¿En qué puedo ayudarte?'),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      })
      this.saveToCache()
    }
  },
  watch: {
    value (val) {
      this.open = val
    },
    open (val) {
      this.$emit('input', val)
      // Concesionarios del grupo para el filtro del Administrador de Grupo
      if (val && this.isGroupAdmin && !this.dealers.length) {
        GrupoPanelService.dealers().then(({ data }) => {
          this.dealers = data.data || []
        }).catch(() => {})
      }
    },
    messages: {
      handler () {
        this.saveToCache()
      },
      deep: true
    },
    historial: {
      handler () {
        this.saveToCache()
      },
      deep: true
    }
  },
  methods: {
    loadFromCache () {
      try {
        const cached = localStorage.getItem('chatbot_data')
        if (cached) {
          const data = JSON.parse(cached)
          this.sessionId = data.sessionId || null
          this.historial = data.historial || []
          this.messages = data.messages || []
        }
      } catch (e) {
        console.warn('Error loading chatbot cache:', e)
      }
    },
    saveToCache () {
      try {
        const data = {
          sessionId: this.sessionId,
          historial: this.historial,
          messages: this.messages
        }
        localStorage.setItem('chatbot_data', JSON.stringify(data))
      } catch (e) {
        console.warn('Error saving chatbot cache:', e)
      }
    },
    clearCache () {
      localStorage.removeItem('chatbot_data')
      this.messages = []
      this.historial = []
      this.sessionId = self.crypto.randomUUID()
      this.messages.push({
        text: '¡Hola! ¿En qué puedo ayudarte?',
        isUser: false,
        rendered: renderMarkdown('¡Hola! ¿En qué puedo ayudarte?'),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      })
      this.saveToCache()
    },
    async sendMessage () {
      if (!this.message.trim() || this.loading) return
      const text = this.message
      const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      this.messages.push({ text, isUser: true, time })
      this.historial.push({ role: 'user', content: text })
      this.message = ''
      this.loading = true
      this.$nextTick(() => this.scrollToBottom())
      try {
        const { data } = await ChatBotService.send({
          mensaje: text,
          url_actual: this.$route.path,
          historial: this.historial,
          session_id: this.sessionId,
          dealer_id: this.isGroupAdmin ? this.dealerId : undefined
        })
        const reply = data.data.data.content
        const { clean, suggestions } = parseSuggestions(reply)
        const replyTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        this.messages.push({ text: reply, isUser: false, rendered: renderMarkdown(clean), time: replyTime, suggestions })
        this.historial.push({ role: 'assistant', content: reply })
      } catch {
        const replyTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        const errorText = 'Ocurrió un error al conectar con el chat. Intenta de nuevo.'
        this.messages.push({
          text: errorText,
          isUser: false,
          rendered: renderMarkdown(errorText),
          time: replyTime
        })
      } finally {
        this.loading = false
        this.$nextTick(() => this.scrollToBottom())
      }
    },
    scrollToBottom () {
      const el = this.$refs.messages
      if (el) el.scrollTop = el.scrollHeight
    },
    sendSuggestion (text) {
      this.message = text
      this.sendMessage()
    }
  }
}
</script>

<style scoped>
/* Ventana de chat */
.chatbot-window {
  transition: all 0.3s ease;
}

/* Status dot en el header */
.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4caf50;
  margin-right: 6px;
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Área de mensajes */
.messages-area {
  flex: 1;
  overflow-y: auto;
  background: linear-gradient(to bottom, #e3f2fd 0%, #bbdefb 100%);
  max-height: 420px;
  scroll-behavior: smooth;
}

.messages-area::-webkit-scrollbar {
  width: 6px;
}

.messages-area::-webkit-scrollbar-track {
  background: transparent;
}

.messages-area::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.messages-area::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Mensajes */
.message-wrapper {
  animation: fadeIn 0.3s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.message-user {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
}

.message-bot {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.bot-avatar {
  flex-shrink: 0;
  margin-top: 4px;
}

.message-bubble {
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 16px;
  position: relative;
  word-wrap: break-word;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.user-bubble {
  background: #1976d2 !important;
  color: white !important;
  border-bottom-right-radius: 4px;
}

.bot-bubble {
  background: white !important;
  color: #2c3e50 !important;
  border-bottom-left-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.message-time {
  font-size: 10px;
  margin-top: 4px;
  opacity: 0.7;
  text-align: right;
}

.user-bubble .message-time {
  color: rgba(255, 255, 255, 0.85);
}

.bot-bubble .message-time {
  color: rgba(0, 0, 0, 0.5);
}

/* Contenido del bot con mejor formato */
.bot-content {
  line-height: 1.5;
}

.bot-content >>> strong {
  font-weight: 700;
  color: var(--v-primary-base);
}

.bot-content >>> em {
  font-style: italic;
  color: #546e7a;
}

.bot-content >>> ul,
.bot-content >>> ol {
  margin: 8px 0;
  padding-left: 20px;
}

.bot-content >>> li {
  margin-bottom: 4px;
}

.bot-content >>> h1,
.bot-content >>> h2,
.bot-content >>> h3 {
  font-weight: 700;
  margin: 8px 0 4px;
  color: var(--v-primary-base);
}

/* Indicador de escritura */
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 14px 18px;
  background: white;
  border-radius: 16px;
  border-bottom-left-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1976d2;
  opacity: 0.8;
  animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes typing {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.6;
  }
  30% {
    transform: translateY(-8px);
    opacity: 1;
  }
}

/* Suggestions */
.suggestions-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.suggestion-chip {
  cursor: pointer !important;
  font-size: 12px !important;
  height: auto !important;
  min-height: 28px !important;
  white-space: normal !important;
  text-align: left;
  padding: 4px 12px !important;
  line-height: 1.3 !important;
}

.suggestion-chip:hover {
  background: var(--v-primary-lighten4) !important;
}

/* Input área */
.message-input >>> .v-input__slot {
  background: #f5f5f5 !important;
  box-shadow: none !important;
  padding: 0 12px !important;
}

.message-input >>> input {
  font-size: 14px;
}

/* Responsive */
@media (max-width: 600px) {
  .chatbot-window {
    width: calc(100vw - 32px) !important;
    max-width: 380px;
  }
}
</style>
